import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

/**
 * Cle de stockage du token JWT en localStorage. ALIGNEE sur la convention du
 * package @siclop/ksadmin-vue (useAdminAuth / restoreAdminAuth ecrivent le JWT
 * sous « jwtToken », en dur). L'auth de theho2 est desormais celle du package
 * (cf stores/auth.store.ts) : l'intercepteur ci-dessous lit la MEME cle, sinon
 * les appels jeu partiraient sans Authorization.
 */
export const AUTH_TOKEN_KEY = 'jwtToken';

/**
 * Client axios partage. baseURL est laisse vide pour que le proxy Vite
 * (cf vite.config.ts : /api -> http://localhost:5000) prenne le relai en
 * dev. En prod on definira VITE_API_BASE_URL.
 */
const baseURL = import.meta.env.VITE_API_BASE_URL ?? '';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// Injection auto du Bearer si token present
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Sur 401, on purge le token et on declenche un event window pour que
 * l'app gere la redirection (sans coupler axios au router/store).
 */
apiClient.interceptors.response.use(
  (resp) => resp,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      window.dispatchEvent(new CustomEvent('theho:unauthorized'));
    }
    return Promise.reject(error);
  },
);

/**
 * Liaison KStone — formes de réponse.
 *
 * Les endpoints KStone ne renvoient PAS tous la même forme :
 *  - GET liste racine (`[HttpGet]` -> KServices.List) : TABLEAU brut.
 *  - GET pagine (`/paged` -> GetPaged) : `{ data, totalCount, ... }`.
 *  - certains endpoints custom : `{ items, total }`.
 *  - POST/PUT CRUD (base.Post/Put) : `DataOperationResult { success, errors, result }`.
 *
 * Lire `data.data` / `data.items` en dur casse dès que la forme diffère
 * (ex. tableau brut -> `undefined` -> `.filter`/`.map` lève une TypeError
 * silencieuse -> « Erreur inconnue »). Ces deux helpers absorbent les formes.
 */

/** Normalise une réponse de LISTE en `T[]` quelle que soit la forme KStone. */
export function extractList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const r = data as { items?: T[]; data?: T[] };
    if (Array.isArray(r.items)) return r.items;
    if (Array.isArray(r.data)) return r.data;
  }
  return [];
}

/** Déballe le modèle d'un `DataOperationResult` (POST/PUT CRUD). Renvoie `data`
 *  tel quel si ce n'est pas un wrapper (endpoints d'action custom). */
export function unwrapResult<T>(data: unknown): T {
  if (
    data && typeof data === 'object'
    && 'result' in data
    && ('success' in data || 'errors' in data)
  ) {
    return (data as { result: T }).result;
  }
  return data as T;
}

/**
 * Extrait un message d'erreur lisible depuis une AxiosError typique.
 */
export function extractApiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { code?: string; message?: string } | undefined;
    if (data?.message) return data.message;
    if (err.message) return err.message;
  }
  return 'Erreur inconnue';
}
